/**
 * WorkloadProfilePicker — single shared affordance for picking a
 * WorkloadProfile across the wizard, the Project Profile card, and the
 * Feasibility rerun panel.
 *
 * Two layouts:
 *  - 'pills' — wide grid of buttons, each wrapped in a shadcn Tooltip
 *    showing label, summary, the impactSummary bullet list, and the
 *    kW/rack density anchor. Used in the wizard Brief step where space
 *    allows.
 *  - 'select' — shadcn Select with rich SelectItem children rendering
 *    label + summary inline. Used in the Profile editor + rerun panel
 *    where the form is column-bound.
 *
 * Both layouts read from the canonical WORKLOAD_PROFILES table — adding a
 * new row to the catalog automatically lights up here without touching the
 * picker.
 */
import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../ui/select';
import { WORKLOAD_PROFILES, getWorkloadProfile, type WorkloadProfile, type WorkloadProfileEntry } from '../../lib/workloadProfile';

export interface WorkloadProfilePickerProps {
  value?: WorkloadProfile;
  onChange: (v: WorkloadProfile) => void;
  /** True when the user deliberately recorded the workload as unknown. */
  explicitUnknown?: boolean;
  /** Records an explicit unknown without inventing a factual workload value. */
  onUnknown?: () => void;
  /** 'pills' (default, wide button grid) or 'select' (compact dropdown). */
  layout?: 'pills' | 'select';
}

const EXPLICIT_UNKNOWN_WORKLOAD_VALUE = '__explicit_unknown__';

export function WorkloadProfilePicker({
  value,
  onChange,
  explicitUnknown = false,
  onUnknown,
  layout = 'pills',
}: WorkloadProfilePickerProps): React.ReactElement {
  if (layout === 'select') {
    // The base-ui SelectValue can't always reflect the rich SelectItem
    // children (it falls back to the raw value). Render the active
    // label ourselves so the trigger always shows the human-readable
    // name.
    //
    // RWX-6 (2026-05-17) — the 2026-05-17 Sickel walkthrough caught
    // the trigger leaking the slug suffix: "AI Training Datacenter
    // ai-training" (W8). Root cause was the dual render — `<span>`
    // with our activeLabel sitting next to `<SelectValue className=
    // "hidden" />`. The `hidden` class hides the SelectValue from
    // layout but the base-ui primitive still inserts the raw `value`
    // string into the trigger's accessible name (and, on at least
    // one browser the user demoed on, into the visible content too).
    // Fix: drop the hidden SelectValue entirely. The trigger child
    // text is now exclusively our human label; the underlying
    // <Select value=…/> still owns the slug for state purposes.
    const activeLabel = (() => {
      if (explicitUnknown) return 'Unknown / not yet decided';
      if (!value) return 'Select a workload';
      try { return getWorkloadProfile(value).label; } catch { return value; }
    })();
    return (
      <Select
        value={explicitUnknown ? EXPLICIT_UNKNOWN_WORKLOAD_VALUE : value ?? null}
        onValueChange={(next) => {
          if (next === EXPLICIT_UNKNOWN_WORKLOAD_VALUE) {
            onUnknown?.();
          } else if (next) {
            onChange(next as WorkloadProfile);
          }
        }}
      >
        <SelectTrigger className="h-9 text-sm" data-testid="workload-profile-select-trigger">
          <span className="text-text-primary">{activeLabel}</span>
        </SelectTrigger>
        <SelectContent>
          {onUnknown ? (
            <SelectItem
              value={EXPLICIT_UNKNOWN_WORKLOAD_VALUE}
              data-testid="workload-profile-select-item-unknown"
            >
              <div className="flex flex-col py-0.5">
                <span className="font-bold text-text-primary">Unknown / not yet decided</span>
                <span className="text-text-muted text-[10px] leading-snug">Keep the workload open and recommend confirmation in reports.</span>
              </div>
            </SelectItem>
          ) : null}
          {WORKLOAD_PROFILES.map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`workload-profile-select-item-${p.id}`}>
              <div className="flex flex-col py-0.5">
                <span className="font-bold text-text-primary">{p.label}</span>
                <span className="text-text-muted text-[10px] leading-snug">{p.summary}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // 'pills' — used by the wizard. Each pill is a Button wrapped in a
  // Tooltip whose content lists impactSummary so users can preview what
  // they're opting into before committing.
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-wrap gap-2">
        {onUnknown ? (
          <Button
            variant={explicitUnknown ? 'default' : 'outline'}
            size="sm"
            onClick={onUnknown}
            aria-pressed={explicitUnknown}
            data-testid="workload-profile-pill-unknown"
          >
            Unknown / not yet decided
          </Button>
        ) : null}
        {WORKLOAD_PROFILES.map((p) => {
          const active = !explicitUnknown && value === p.id;
          return (
            <Tooltip key={p.id}>
              <TooltipTrigger asChild>
                <Button
                  variant={active ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onChange(p.id)}
                  aria-pressed={active}
                  className="rounded-md uppercase tracking-widest text-[10px]"
                  data-testid={`workload-profile-pill-${p.id}`}
                >
                  {p.label}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-sm bg-surface-2 border-border text-text-primary p-3">
                <ImpactDetail profile={p} />
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

/**
 * Reusable detail block — the "What this changes" body. Used inside the
 * pill tooltip and in the read-mode Profile Card "view details" disclosure.
 */
export function ImpactDetail({ profile }: { profile: WorkloadProfileEntry }): React.ReactElement {
  return (
    <div data-testid={`workload-profile-impact-${profile.id}`}>
      <div className="font-bold uppercase tracking-widest text-[10px] mb-1">{profile.label}</div>
      <div className="text-[11px] text-text-secondary mb-2">{profile.summary}</div>
      <div className="type-meta text-text-muted mb-1">What this changes</div>
      <ul className="list-disc pl-4 space-y-1 text-[11px] text-text-primary">
        {profile.impactSummary.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <div className="text-[10px] text-text-muted mt-2">
        Power density anchor: {profile.powerDensityKwRack.min}–{profile.powerDensityKwRack.max} kW/rack
      </div>
    </div>
  );
}

/** Convenience helper for "<from-label> → <to-label>" diff rendering. */
export function workloadProfileLabel(id: WorkloadProfile | string | undefined): string {
  if (!id) return 'Mixed / Not yet decided';
  try {
    return getWorkloadProfile(id as WorkloadProfile).label;
  } catch {
    return String(id);
  }
}
